import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Clock, HeartPulse, ListTodo, CheckSquare, PenLine, Timer, CalendarDays, Bell, Users, Settings2, Loader2, ChevronUp, ChevronDown, Eye, EyeOff, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const fmt = (min) => `${Math.floor(min / 60)}h ${min % 60}m`;
const WIDGET_META = {
  jornada: { icon: Clock, link: "/os/workforce" },
  pulso: { icon: HeartPulse, link: "/os/bienestar" },
  tareas: { icon: ListTodo, link: "/os/herramientas" },
  aprobaciones: { icon: CheckSquare, link: "/os/aprobaciones" },
  firmas: { icon: PenLine, link: "/os/sign" },
  tiempo: { icon: Timer, link: "/os/tiempo" },
  turno: { icon: CalendarDays, link: "/os/workforce" },
  notificaciones: { icon: Bell, link: "/os" },
  equipo: { icon: Users, link: "/os/plantilla" },
};

export default function PersonalDashboard() {
  const nav = useNavigate();
  const [layout, setLayout] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [data, setData] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, d] = await Promise.all([api.get("/mydash/layout"), api.get("/mydash/data")]);
      setLayout(l.data.widgets);
      setCatalog(l.data.catalog);
      setData(d.data);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= layout.length) return;
    const next = [...layout];
    [next[i], next[j]] = [next[j], next[i]];
    setLayout(next);
  };
  const toggle = (i) => setLayout(layout.map((w, idx) => (idx === i ? { ...w, visible: !w.visible } : w)));

  const save = async () => {
    setSaving(true);
    try { await api.put("/mydash/layout", { widgets: layout }); toast.success("Panel guardado"); setEditing(false); }
    catch { toast.error("Error al guardar"); } finally { setSaving(false); }
  };

  if (!layout) return <OSLayout title="OMNIA Personal Dashboard"><div className="py-20 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div></OSLayout>;

  const visible = layout.filter((w) => w.visible && data[w.key] !== undefined);

  return (
    <OSLayout title="OMNIA Personal Dashboard">
      <div className="space-y-6" data-testid="mydash-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-navy/50 text-sm"><LayoutDashboard className="w-4 h-4" /> Tu espacio personalizable</div>
          <button onClick={() => setEditing(!editing)} className={`btn text-sm gap-2 ${editing ? "btn-ghost" : "btn-primary"}`} data-testid="mydash-edit-toggle">
            {editing ? <><X className="w-4 h-4" /> Cerrar edición</> : <><Settings2 className="w-4 h-4" /> Personalizar</>}
          </button>
        </div>

        {editing ? (
          <div className="bg-white border border-navy/10 rounded-2xl p-5" data-testid="mydash-editor">
            <div className="text-sm font-medium mb-3">Organiza tus widgets</div>
            <div className="space-y-2">
              {layout.map((w, i) => (
                <div key={w.key} className="flex items-center gap-3 border border-navy/10 rounded-xl px-3 py-2" data-testid={`mydash-row-${w.key}`}>
                  <div className="flex flex-col">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="text-navy/40 hover:text-navy disabled:opacity-20" data-testid={`mydash-up-${w.key}`}><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === layout.length - 1} className="text-navy/40 hover:text-navy disabled:opacity-20" data-testid={`mydash-down-${w.key}`}><ChevronDown className="w-4 h-4" /></button>
                  </div>
                  <span className="flex-1 text-sm font-medium">{(catalog.find((c) => c.key === w.key) || {}).label || w.key}</span>
                  <button onClick={() => toggle(i)} className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${w.visible ? "bg-emerald-500/15 text-emerald-600" : "bg-navy/5 text-navy/40"}`} data-testid={`mydash-toggle-${w.key}`}>
                    {w.visible ? <><Eye className="w-3.5 h-3.5" /> Visible</> : <><EyeOff className="w-3.5 h-3.5" /> Oculto</>}
                  </button>
                </div>
              ))}
            </div>
            <button onClick={save} disabled={saving} className="btn btn-primary mt-4" data-testid="mydash-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar panel"}</button>
          </div>
        ) : (
          visible.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-8 text-center text-sm text-navy/40">No hay widgets visibles. Pulsa "Personalizar" para activarlos.</div>
          : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="mydash-grid">
            {visible.map((w) => <Widget key={w.key} wkey={w.key} label={(catalog.find((c) => c.key === w.key) || {}).label} data={data[w.key]} nav={nav} />)}
          </div>
        )}
      </div>
    </OSLayout>
  );
}

function Widget({ wkey, label, data, nav }) {
  const meta = WIDGET_META[wkey] || {};
  const Ic = meta.icon || LayoutDashboard;
  return (
    <button onClick={() => meta.link && nav(meta.link)} className="text-left bg-white border border-navy/10 rounded-2xl p-5 hover:border-violet hover:shadow-md transition-all group" data-testid={`mydash-widget-${wkey}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-navy/50 text-sm"><Ic className="w-4 h-4" /> {label}</div>
        <ArrowRight className="w-4 h-4 text-navy/20 group-hover:text-violet transition-colors" />
      </div>
      <div className="mt-3">{renderBody(wkey, data)}</div>
    </button>
  );
}

function Big({ children, sub }) {
  return <div><div className="font-editorial text-3xl">{children}</div>{sub && <div className="text-xs text-navy/40 mt-0.5">{sub}</div>}</div>;
}

function renderBody(k, d) {
  if (!d) return <div className="text-sm text-navy/30">—</div>;
  switch (k) {
    case "jornada":
      return d.clocked_in ? <Big sub="Jornada en curso"><span className="text-emerald-600">Activa</span></Big>
        : d.auto_closed ? <Big sub="Cerrada automáticamente"><span className="text-gold">Revisar</span></Big>
        : d.has_record ? <Big sub="Jornada finalizada">Cerrada</Big>
        : <Big sub="Aún no inicias"><span className="text-navy/50">Pendiente</span></Big>;
    case "pulso":
      return d.pending ? <div><div className="text-sm font-medium text-violet">Tienes un pulso</div><div className="text-xs text-navy/50 mt-1 line-clamp-2">{d.question}</div></div>
        : <Big sub="Sin pulsos hoy"><span className="text-emerald-600">Al día</span></Big>;
    case "tareas":
      return <div><Big sub={d.count === 1 ? "tarea pendiente" : "tareas pendientes"}>{d.count}</Big>
        {d.items?.length > 0 && <ul className="mt-2 space-y-1">{d.items.slice(0, 3).map((t, i) => <li key={i} className="text-xs text-navy/60 truncate">• {t.title}</li>)}</ul>}</div>;
    case "aprobaciones":
      return <div><Big sub="por aprobar">{d.count}</Big>
        {d.items?.length > 0 && <ul className="mt-2 space-y-1">{d.items.slice(0, 3).map((a, i) => <li key={i} className="text-xs text-navy/60 truncate">• {a.tipo} — {a.solicitante}</li>)}</ul>}</div>;
    case "firmas":
      return <Big sub={d.count === 1 ? "documento por firmar" : "documentos por firmar"}>{d.count}</Big>;
    case "tiempo":
      return <Big sub={d.active_project ? `En curso: ${d.active_project}` : "registrado hoy"}>{fmt(d.total_min)}</Big>;
    case "turno":
      return d.has_shift ? <Big sub={d.label || "Turno de hoy"}>{d.start}–{d.end}</Big> : <Big sub="Sin turno asignado"><span className="text-navy/40">—</span></Big>;
    case "notificaciones":
      return <Big sub="sin leer">{d.unread}</Big>;
    case "equipo":
      return <div><Big sub={`de ${d.headcount} colaboradores`}>{d.present_today} presentes</Big>
        <div className="text-xs text-navy/50 mt-1">{d.vacantes} vacantes abiertas</div></div>;
    default:
      return <div className="text-sm text-navy/30">—</div>;
  }
}
